import { withFeedback } from '../lib/buttonFeedback.js';

const APP_URL = 'https://ravenresume.com';

export class UserManager {
    constructor(options = {}) {
        this._db = options.db;
        this._auth = options.auth;
        this._statusBar = options.statusBar;
        this._currentUserEmail = options.currentUserEmail;
        this._users = [];
        this._accessRequests = [];
        this._unsub = null;
        this._unsubRequests = null;
        this._el = document.createElement('div');
        this._el.className = 'rm-config-panel';
    }

    get element() { return this._el; }

    init() {
        this._unsub = this._db.collection('app-users')
            .orderBy('createdAt', 'desc')
            .onSnapshot(snap => {
                this._users = snap.docs.map(d => ({ email: d.id, ...d.data() }));
                this.render();
            });

        this._unsubRequests = this._db.collection('access-requests')
            .orderBy('createdAt', 'desc')
            .onSnapshot(snap => {
                this._accessRequests = snap.docs.map(d => ({ id: d.id, ...d.data() }));
                this.render();
            });
    }

    render() {
        this._el.innerHTML = '';

        // Pending access requests section
        const pendingRequests = this._accessRequests.filter(r => r.status === 'pending');
        if (pendingRequests.length > 0) {
            const requestSection = document.createElement('div');
            requestSection.className = 'rm-config-section';
            requestSection.innerHTML = `<h3>Pending Access Requests (${pendingRequests.length})</h3>`;

            const table = document.createElement('table');
            table.className = 'rm-table';
            table.style.tableLayout = 'auto';
            table.innerHTML = `
                <thead><tr>
                    <th>Email</th>
                    <th>Name</th>
                    <th>Reason</th>
                    <th>Date</th>
                    <th>Actions</th>
                </tr></thead>
            `;
            const tbody = document.createElement('tbody');
            for (const req of pendingRequests) {
                const tr = document.createElement('tr');
                const date = req.createdAt?.toDate?.() ? req.createdAt.toDate().toLocaleDateString() : 'N/A';
                tr.innerHTML = `
                    <td>${this._esc(req.email)}</td>
                    <td>${this._esc(req.name)}</td>
                    <td style="max-width:200px;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;" title="${this._esc(req.reason || '')}">${this._esc(req.reason || '-')}</td>
                    <td>${date}</td>
                    <td></td>
                `;
                const actionCell = tr.querySelector('td:last-child');

                const approveBtn = document.createElement('button');
                approveBtn.className = 'rm-btn rm-btn-primary';
                approveBtn.style.cssText = 'padding:4px 8px;font-size:11px;margin-right:4px;';
                approveBtn.textContent = 'Approve';
                approveBtn.addEventListener('click', () => withFeedback(approveBtn, () => this._approveRequest(req), { successText: '\u2713 Approved' }));
                actionCell.appendChild(approveBtn);

                const denyBtn = document.createElement('button');
                denyBtn.className = 'rm-btn rm-btn-danger';
                denyBtn.style.cssText = 'padding:4px 8px;font-size:11px;';
                denyBtn.textContent = 'Deny';
                denyBtn.addEventListener('click', () => withFeedback(denyBtn, () => this._denyRequest(req), { successText: '\u2713 Denied' }));
                actionCell.appendChild(denyBtn);

                tbody.appendChild(tr);
            }
            table.appendChild(tbody);
            requestSection.appendChild(table);
            this._el.appendChild(requestSection);
        }

        // Invite section
        const inviteSection = document.createElement('div');
        inviteSection.className = 'rm-config-section';
        inviteSection.innerHTML = `
            <h3>Invite New User</h3>
            <div style="display:flex;gap:8px;align-items:flex-end;">
                <div class="rm-form-row" style="flex:1;">
                    <label style="font-size:12px;color:var(--text-muted);font-weight:600;">Email Address</label>
                    <input type="email" id="rmInviteEmail" placeholder="user@example.com"
                        style="background:var(--bg-secondary);color:var(--text);border:1px solid var(--border);border-radius:6px;padding:8px 12px;font-size:14px;">
                </div>
                <div class="rm-form-row">
                    <label style="font-size:12px;color:var(--text-muted);font-weight:600;">Role</label>
                    <select id="rmInviteRole" style="background:var(--bg-secondary);color:var(--text);border:1px solid var(--border);border-radius:6px;padding:8px 12px;font-size:14px;">
                        <option value="user">User</option>
                        <option value="admin">Admin</option>
                    </select>
                </div>
                <button class="rm-btn rm-btn-primary" id="rmInviteBtn" style="height:38px;">Invite</button>
            </div>
        `;
        const inviteBtn = inviteSection.querySelector('#rmInviteBtn');
        inviteBtn.addEventListener('click', () => withFeedback(inviteBtn, () => this._invite(inviteSection), { successText: '\u2713 Invited' }));
        this._el.appendChild(inviteSection);

        // User list
        const listSection = document.createElement('div');
        listSection.className = 'rm-config-section';
        listSection.innerHTML = '<h3>Users</h3>';

        if (this._users.length === 0) {
            listSection.innerHTML += '<div style="color:var(--text-muted);font-size:13px;padding:12px 0;">No users yet.</div>';
        } else {
            const table = document.createElement('table');
            table.className = 'rm-table';
            table.style.tableLayout = 'auto';
            table.innerHTML = `
                <thead><tr>
                    <th>Email</th>
                    <th>Role</th>
                    <th>Status</th>
                    <th>Actions</th>
                </tr></thead>
            `;
            const tbody = document.createElement('tbody');
            for (const user of this._users) {
                const tr = document.createElement('tr');
                const isSelf = user.email === this._currentUserEmail;
                tr.innerHTML = `
                    <td>${this._esc(user.email)}${isSelf ? ' <span style="color:var(--primary);font-size:11px;">(you)</span>' : ''}</td>
                    <td><span class="role-badge role-${user.role}">${user.role}</span></td>
                    <td><span class="rm-status rm-status-${user.status === 'active' ? 'ready' : 'pending'}">${user.status || 'invited'}</span></td>
                    <td></td>
                `;
                const actionCell = tr.querySelector('td:last-child');

                if (!isSelf) {
                    // Resend invite button (only for invited users)
                    if (user.status === 'invited') {
                        const inviteBtn = document.createElement('button');
                        inviteBtn.className = 'rm-btn rm-btn-primary';
                        inviteBtn.style.cssText = 'padding:4px 8px;font-size:11px;margin-right:4px;';
                        inviteBtn.textContent = 'Resend Invite';
                        inviteBtn.addEventListener('click', () => withFeedback(inviteBtn, () => this._resendInvite(user.email, user.role), { successText: '\u2713 Sent' }));
                        actionCell.appendChild(inviteBtn);
                    }

                    // Toggle role button
                    const roleBtn = document.createElement('button');
                    roleBtn.className = 'rm-btn';
                    roleBtn.style.cssText = 'padding:4px 8px;font-size:11px;margin-right:4px;';
                    roleBtn.textContent = user.role === 'admin' ? 'Demote' : 'Promote';
                    roleBtn.addEventListener('click', () => withFeedback(roleBtn, () => this._toggleRole(user.email, user.role)));
                    actionCell.appendChild(roleBtn);

                    // Delete button
                    const delBtn = document.createElement('button');
                    delBtn.className = 'rm-btn rm-btn-danger';
                    delBtn.style.cssText = 'padding:4px 8px;font-size:11px;';
                    delBtn.textContent = 'Delete';
                    delBtn.addEventListener('click', () => withFeedback(delBtn, () => this._deleteUser(user.email), { successText: '\u2713 Deleted' }));
                    actionCell.appendChild(delBtn);
                }

                tbody.appendChild(tr);
            }
            table.appendChild(tbody);
            listSection.appendChild(table);
        }

        this._el.appendChild(listSection);
    }

    async _invite(section) {
        const emailInput = section.querySelector('#rmInviteEmail');
        const roleSelect = section.querySelector('#rmInviteRole');
        const email = emailInput.value.trim().toLowerCase();
        const role = roleSelect.value;

        if (!email || !email.includes('@')) {
            if (this._statusBar) this._statusBar.log('Please enter a valid email address', 'warning');
            return;
        }

        try {
            await this._db.collection('app-users').doc(email).set({
                role,
                status: 'invited',
                createdAt: firebase.firestore.FieldValue.serverTimestamp(),
                invitedBy: this._currentUserEmail,
            });

            // Send invite email
            await this._db.collection('resume-emails').add({
                candidateId: 'system',
                projectId: 'system',
                to: email,
                cc: [this._currentUserEmail],
                from: 'hiring@ravenresume.com',
                fromName: 'Raven Resume',
                replyTo: this._currentUserEmail,
                subject: 'You have been invited to Raven Resume',
                body: `Hi,\n\nYou have been invited to use Raven Resume, an AI-powered resume screening tool.\n\nVisit the app and enter your email to receive a sign-in link:\n${APP_URL}\n\nYou will have access as ${role === 'admin' ? 'an Admin' : 'a User'}. If you have any questions, reply to this email.\n\nBest,\nRaven Resume`,
                status: 'pending',
                direction: 'outbound',
                sendgridMessageId: null,
                sendgridEvents: [],
                calendarAccepted: false,
                error: null,
                createdAt: firebase.firestore.FieldValue.serverTimestamp(),
                updatedAt: firebase.firestore.FieldValue.serverTimestamp(),
                sentAt: null,
            });

            emailInput.value = '';
            if (this._statusBar) this._statusBar.log(`Invited ${email} as ${role} — invite email sent`, 'success');
        } catch (err) {
            if (this._statusBar) this._statusBar.log(`Failed to invite: ${err.message}`, 'error');
            throw err;
        }
    }

    async _resendInvite(email, role) {
        try {
            await this._db.collection('resume-emails').add({
                candidateId: 'system',
                projectId: 'system',
                to: email,
                cc: [this._currentUserEmail],
                from: 'hiring@ravenresume.com',
                fromName: 'Raven Resume',
                replyTo: this._currentUserEmail,
                subject: 'You have been invited to Raven Resume',
                body: `Hi,\n\nYou have been invited to use Raven Resume, an AI-powered resume screening tool.\n\nVisit the app and enter your email to receive a sign-in link:\n${APP_URL}\n\nYou will have access as ${role === 'admin' ? 'an Admin' : 'a User'}. If you have any questions, reply to this email.\n\nBest,\nRaven Resume`,
                status: 'pending',
                direction: 'outbound',
                sendgridMessageId: null,
                sendgridEvents: [],
                calendarAccepted: false,
                error: null,
                createdAt: firebase.firestore.FieldValue.serverTimestamp(),
                updatedAt: firebase.firestore.FieldValue.serverTimestamp(),
                sentAt: null,
            });
            if (this._statusBar) this._statusBar.log(`Invite resent to ${email}`, 'success');
        } catch (err) {
            if (this._statusBar) this._statusBar.log(`Failed to resend: ${err.message}`, 'error');
            throw err;
        }
    }

    async _toggleRole(email, currentRole) {
        const newRole = currentRole === 'admin' ? 'user' : 'admin';
        try {
            await this._db.collection('app-users').doc(email).update({ role: newRole });
            if (this._statusBar) this._statusBar.log(`${email} is now ${newRole}`, 'success');
        } catch (err) {
            if (this._statusBar) this._statusBar.log(`Failed: ${err.message}`, 'error');
            throw err;
        }
    }

    async _deleteUser(email) {
        if (!confirm(`Delete user ${email}? They will lose access.`)) return;
        try {
            await this._db.collection('app-users').doc(email).delete();
            if (this._statusBar) this._statusBar.log(`Deleted ${email}`, 'info');
        } catch (err) {
            if (this._statusBar) this._statusBar.log(`Failed: ${err.message}`, 'error');
            throw err;
        }
    }

    async _approveRequest(req) {
        try {
            // Create app-users doc
            await this._db.collection('app-users').doc(req.email).set({
                role: 'user',
                status: 'approved',
                name: req.name || '',
                createdAt: firebase.firestore.FieldValue.serverTimestamp(),
                approvedBy: this._currentUserEmail,
            });

            // Delete the access request
            await this._db.collection('access-requests').doc(req.id).delete();

            // Send a magic link email via resume-emails collection
            await this._db.collection('resume-emails').add({
                candidateId: 'system',
                projectId: 'system',
                to: req.email,
                cc: [this._currentUserEmail],
                from: 'hiring@ravenresume.com',
                fromName: 'Raven Resume',
                replyTo: this._currentUserEmail,
                subject: 'Your Raven Resume access has been approved',
                body: `Hi ${req.name || ''},\n\nYour request to access Raven Resume has been approved.\n\nVisit the app and enter your email to receive a sign-in link:\n${APP_URL}\n\nIf you have any questions, reply to this email.\n\nBest,\nRaven Resume`,
                status: 'pending',
                direction: 'outbound',
                sendgridMessageId: null,
                sendgridEvents: [],
                calendarAccepted: false,
                error: null,
                createdAt: firebase.firestore.FieldValue.serverTimestamp(),
                updatedAt: firebase.firestore.FieldValue.serverTimestamp(),
                sentAt: null,
            });

            if (this._statusBar) this._statusBar.log(`Approved ${req.email} — notification email queued`, 'success');
        } catch (err) {
            if (this._statusBar) this._statusBar.log(`Failed to approve: ${err.message}`, 'error');
            throw err;
        }
    }

    async _denyRequest(req) {
        if (!confirm(`Deny access request from ${req.email}?`)) return;
        try {
            await this._db.collection('access-requests').doc(req.id).delete();
            if (this._statusBar) this._statusBar.log(`Denied request from ${req.email}`, 'info');
        } catch (err) {
            if (this._statusBar) this._statusBar.log(`Failed to deny: ${err.message}`, 'error');
            throw err;
        }
    }

    dispose() {
        if (this._unsub) this._unsub();
        if (this._unsubRequests) this._unsubRequests();
    }

    _esc(str) {
        const d = document.createElement('div');
        d.textContent = str || '';
        return d.innerHTML;
    }
}
